import React, { useEffect, useMemo, useState } from "react";
import TableDesign, {
  TableHeading,
} from "../../../components/common/table/TableDesign";
import {
  Box,
  TableCell,
  TableRow,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Button,
  MenuItem,
} from "@mui/material";
import useStyles from "../../../components/pageTitle/styles";
import CustomButton from "../../../components/common/button/CustomButton";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  getAllComplaintsAction,
  setAddComplaintResponse,
} from "../../../store/reducers/complaintSlice";
import dayjs from "dayjs";
import ComplaintDetailModal from "./ComplaintDetailModal";
import { useNavigate } from "react-router-dom";

// Stage options for filtering
const COMPLAINT_STAGES = [
  "All",
  "Initiated",
  "Assigned: user handler",
  "Data acquiring",
  "Waiting customer",
  "Waiting quote",
  "Waiting repairer",
  "With insurers",
  "Offer made",
  "Offer accepted",
  "Court papers",
  "Court date set",
  "Settled/Resolved",
  "Unresolved",
];

interface FilterOption {
  page: number;
  limit: number;
  orderBy: string;
  orderIn: string;
  status: string;
}

const ComplaintsList = () => {
  const classes = useStyles();
  const { complaints,pagination, isAddComplaintSuccess } = useSelector(
    (state: any) => state?.complaint,
  );
  const { userProfile } = useSelector((state: any) => state?.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState<any>(null);
  const [filterOption, setFilterOption] = React.useState<FilterOption>({
    page: 1,
    limit: 10,
    orderBy: "",
    orderIn: "asc",
    status: "Initiated",
  });

  useEffect(() => {
    if (filterOption.status) {
      dispatch(
        getAllComplaintsAction({
          ...filterOption,
          status: filterOption.status === "All" ? "" : filterOption.status,
        }),
      );
    }
  }, [dispatch, filterOption]);

  useEffect(() => {
    if (isAddComplaintSuccess) {
      dispatch(
        getAllComplaintsAction({
          ...filterOption,
          status: filterOption.status === "All" ? "" : filterOption.status,
        }),
      );
      dispatch(setAddComplaintResponse(false));
      setShowModal(false);
    }
  }, [dispatch, filterOption, isAddComplaintSuccess]);

  // Filter complaints based on status
  const filteredComplaints = useMemo(() => {
    if (filterOption.status === "All") {
      return complaints;
    }
    return complaints?.filter(
      (item: any) => item.status === filterOption.status,
    );
  }, [filterOption.status, complaints]);

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    setFilterOption({
      ...filterOption,
      status: event.target.value,
    });
  };

  const handleResetFilters = () => {
    setFilterOption({
      ...filterOption,
      status: "Initiated",
    });
  };

  const handleEditClick = (complaint: any) => {
    setSelectedComplaint(complaint);
    setShowModal(true);
  };

  const handleLeadClick = (leadId: string) => {
    if (!leadId) return;
    sessionStorage.setItem("selectedJobLeadId", String(leadId));
    navigate("/dashboard");
  };

  const tableHeadings: TableHeading[] = [
    { label: "Action", align: "left" },
    { label: "Complaint ID", align: "left" },
    { label: "Lead ID", align: "left" },
    { label: "Customer Name", align: "left" },
    { label: "Service", align: "left" },
    { label: "Service Date", align: "left" },
    { label: "Reported Date", align: "left" },
    { label: "Title", align: "left" },
    { label: "Reported By", align: "left" },
    { label: "Insurance", align: "left" },
    { label: "Crew", align: "left" },
    { label: "Handler", align: "left" },
    { label: "Costs", align: "left" },
    { label: "Recovered", align: "left" },
    { label: "Status", align: "left" },
  ];
  return (
    <>
      <div className={classes.pageTitleContainer}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent={"center"}
          gap={2}
          mb={2}
        >
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="status-filter-label">Filter by Stage</InputLabel>
            <Select
              labelId="status-filter-label"
              id="status-filter"
              value={filterOption.status}
              label="Filter by Stage"
              onChange={handleStatusChange}
              sx={{
                fontSize: "0.875rem",
                height: 30,
              }}
            >
              {COMPLAINT_STAGES.map((stage) => (
                <MenuItem
                  key={stage}
                  value={stage}
                  sx={{ fontSize: "0.875rem" }}
                >
                  {stage}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            size="small"
            sx={{ ml: 1, textTransform: "none", height: 30 }}
            onClick={handleResetFilters}
          >
            Reset Filters
          </Button>
        </Box>
      </div>
      <Box
        sx={{
          bgcolor: "#fff",
          height: {
            xs: "auto",
            sm: filteredComplaints?.length > 2 ? "auto" : "40vh",
            maxHeight:'100%'
          },
          border: "1px solid #d1cfcf",
          borderRadius: "5px",
          padding: "0px",
          // overflow: "hidden",
        }}
      >
        <TableDesign
          tableHeadings={tableHeadings}
          tableHeadColor={"#02906f"}
          cellColor={"#fff"}
          pagination={filteredComplaints?.length > 0 ? pagination : null}
          isLoader={false}
          filterOption={filterOption}
          stickyHeader={true}
          setFilterOption={setFilterOption}
          extraCSS={{
            height: "calc(100%)",
          }}
        >
          {filteredComplaints?.length > 0 ? (
            filteredComplaints?.map((val: any, idx: number) => (
              <TableRow
                key={val?.complaint_id}
                sx={{
                  cursor: "pointer",
                  marginTop: "10px",
                  padding: "4px !important",
                  borderRadius: "20px !important",
                  height: "20px !important",
                  "&:hover": {
                    backgroundColor: "#f0f0f0",
                    outline: "2px solid #f0f0f0",
                    borderRadius: "0px !important",
                    outlineOffset: "-2px !important",
                  },
                }}
              >
                <TableCell align="left">
                  <CustomButton
                    title={"Edit"}
                    variant="outlined"
                    onClick={() => handleEditClick(val)}
                  />
                </TableCell>
                <TableCell>{val?.complaint_id}</TableCell>
                <TableCell>
                  {val?.lead_id ? (
                    <span
                      role="button"
                      tabIndex={0}
                      style={{
                        color: "#02906f",
                        textDecoration: "none",
                        cursor: "pointer",
                        fontWeight: 600,
                      }}
                      onClick={() => handleLeadClick(val?.lead_id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleLeadClick(val?.lead_id);
                        }
                      }}
                    >
                      {val?.lead_id}
                    </span>
                  ) : (
                    ""
                  )}
                </TableCell>
                <TableCell>{val?.customer_name}</TableCell>
                <TableCell>{val?.job_name}</TableCell>
                <TableCell>
                  {val.job_date
                    ? dayjs(val.job_date).format(
                        userProfile?.dateFormat
                          ? userProfile?.dateFormat
                          : "DD/MM/YYYY",
                      )
                    : ""}
                </TableCell>
                <TableCell>
                  {val.reported_date
                    ? dayjs(val.reported_date).format(
                        userProfile?.dateFormat
                          ? userProfile?.dateFormat
                          : "DD/MM/YYYY",
                      )
                    : ""}
                </TableCell>
                <TableCell>{val?.title}</TableCell>
                <TableCell>{val?.reported_by}</TableCell>
                <TableCell>{val?.insurance_type}</TableCell>
                <TableCell
                  width={"180px"}
                  sx={{
                    whiteSpace: "normal",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {val?.crew
                    ?.map((crewMember: any) => crewMember?.name)
                    .join(", ")}
                </TableCell>
                <TableCell>{val?.handled_by_name}</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {val?.costs ? `£ ${val?.costs}` : ""}
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {val?.recovered ? `£ ${val?.recovered}` : ""}
                </TableCell>
                <TableCell>{val?.status}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={tableHeadings?.length} align="center">
                <Box
                  sx={{
                    height: "60vh",
                    display: "flex",
                    // alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                  }}
                >
                  <span
                    style={{
                      position: "absolute",
                      fontSize: "1rem",
                      color: "#4A4A4A",
                      top: "100px",
                    }}
                  >
                    {`No Data Found!`}
                  </span>
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableDesign>
      </Box>

      {/* Complaint Detail Modal */}
      {showModal && (
        <ComplaintDetailModal
          show={showModal}
          setShow={setShowModal}
          complaintData={selectedComplaint}
        />
      )}
    </>
  );
};

export default ComplaintsList;
